import { AccountInfo, BrowserCacheLocation, PublicClientApplication } from "@azure/msal-browser";

export const BC_SCOPE = "https://api.businesscentral.dynamics.com/.default";

export function formatAuthError(err: unknown): string {
  const maybe = err as { errorCode?: string; message?: string };
  const code = String(maybe?.errorCode ?? "").trim();
  const message = String(maybe?.message ?? "").trim();
  if (code && message) return `${code}: ${message}`;
  if (code) return code;
  return message || "unknown error";
}

export function clearMsalStorageEntries(clientId: string, storages: Storage[]): void {
  const clientIdLower = clientId.toLowerCase();
  for (const storage of storages) {
    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && (key.startsWith("msal.") || key.toLowerCase().includes(clientIdLower))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => storage.removeItem(k));
  }
}

export function buildAuthRedirectUri(origin: string): string {
  return `${origin}/auth-redirect.html`;
}

export function buildMsalAuthority(tenantId?: string): string {
  const tenant = tenantId?.trim();
  return tenant
    ? `https://login.microsoftonline.com/${tenant}`
    : "https://login.microsoftonline.com/common";
}

export function createMsalClient(
  clientId: string,
  origin: string,
  cacheLocation: BrowserCacheLocation = BrowserCacheLocation.LocalStorage,
  tenantId?: string,
): PublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId,
      authority: buildMsalAuthority(tenantId),
      redirectUri: buildAuthRedirectUri(origin),
    },
    cache: {
      cacheLocation,
      storeAuthStateInCookie: false,
    },
  });
}

export function getPreferredAccount(
  msal: PublicClientApplication,
  currentAccount: AccountInfo | null,
  loginHint?: string,
): AccountInfo | null {
  if (currentAccount) return currentAccount;

  const active = msal.getActiveAccount();
  if (active) return active;

  const normalizedHint = loginHint?.toLowerCase();
  const accounts = msal.getAllAccounts();
  if (accounts.length === 0) return null;

  if (normalizedHint) {
    const hinted = accounts.find((a) => a.username?.toLowerCase() === normalizedHint);
    if (hinted) return hinted;
  }

  return accounts[0];
}
